import { useEffect, useRef } from "react";

/**
 * Full-viewport canvas of soft dots that flow away from the cursor and
 * spring back to their anchor. Disabled on coarse pointers and
 * prefers-reduced-motion. Mounted once at the root for all public pages.
 */
export function FlowingDotsCursor() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    // Dot grid spacing (CSS px). Larger = fewer dots = cheaper.
    const SPACING = 30;
    const RADIUS = 1.4;
    const INFLUENCE = 180; // px, repulsion radius
    const STRENGTH = 1400; // repulsion magnitude
    const SPRING = 0.06;
    const DAMP = 0.86;

    // Colors
    const BASE = { r: 60, g: 74, b: 102 };   // #3C4A66
    const HOT  = { r: 224, g: 122, b: 95 };  // #E07A5F

    type Dot = { ax: number; ay: number; x: number; y: number; vx: number; vy: number; phase: number };
    let dots: Dot[] = [];
    let width = 0;
    let height = 0;
    let mouseX = -9999;
    let mouseY = -9999;
    let raf = 0;
    let lastT = performance.now();
    let visible = true;

    const layout = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * DPR);
      canvas.height = Math.floor(height * DPR);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

      dots = [];
      const cols = Math.ceil(width / SPACING) + 1;
      const rows = Math.ceil(height / SPACING) + 1;
      const offsetX = (width - (cols - 1) * SPACING) / 2;
      const offsetY = (height - (rows - 1) * SPACING) / 2;
      for (let j = 0; j < rows; j++) {
        for (let i = 0; i < cols; i++) {
          const ax = offsetX + i * SPACING;
          const ay = offsetY + j * SPACING;
          dots.push({
            ax, ay, x: ax, y: ay, vx: 0, vy: 0,
            phase: Math.random() * Math.PI * 2,
          });
        }
      }
    };

    const onMove = (e: PointerEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    const onLeave = () => { mouseX = -9999; mouseY = -9999; };
    const onVisibility = () => { visible = document.visibilityState === "visible"; };

    layout();
    window.addEventListener("resize", layout);
    if (!coarse) {
      window.addEventListener("pointermove", onMove, { passive: true });
      window.addEventListener("pointerleave", onLeave);
    }
    document.addEventListener("visibilitychange", onVisibility);

    const tick = (t: number) => {
      raf = requestAnimationFrame(tick);
      if (!visible) { lastT = t; return; }
      const dt = Math.min((t - lastT) / 16.67, 2.5); // normalized to ~60fps frames
      lastT = t;

      ctx.clearRect(0, 0, width, height);

      const infl2 = INFLUENCE * INFLUENCE;

      for (let k = 0; k < dots.length; k++) {
        const d = dots[k];

        if (coarse) {
          // Slow ambient drift for touch devices
          d.phase += 0.008 * dt;
          d.x = d.ax + Math.cos(d.phase) * 2;
          d.y = d.ay + Math.sin(d.phase * 0.8) * 2;
        } else {
          const dx = d.x - mouseX;
          const dy = d.y - mouseY;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < infl2 && dist2 > 1) {
            const force = STRENGTH / dist2;
            const dist = Math.sqrt(dist2);
            d.vx += (dx / dist) * force * 0.012 * dt;
            d.vy += (dy / dist) * force * 0.012 * dt;
          }
          // Spring back to anchor
          d.vx += (d.ax - d.x) * SPRING * dt;
          d.vy += (d.ay - d.y) * SPRING * dt;
          d.vx *= DAMP;
          d.vy *= DAMP;
          d.x += d.vx * dt;
          d.y += d.vy * dt;
        }

        // Color: lerp base→hot by proximity to cursor
        let mix = 0;
        if (!coarse) {
          const dx2 = d.x - mouseX;
          const dy2 = d.y - mouseY;
          const dist2b = dx2 * dx2 + dy2 * dy2;
          if (dist2b < infl2) mix = 1 - dist2b / infl2;
        }
        const r = BASE.r + (HOT.r - BASE.r) * mix;
        const g = BASE.g + (HOT.g - BASE.g) * mix;
        const b = BASE.b + (HOT.b - BASE.b) * mix;
        const alpha = 0.45 + mix * 0.5;
        const radius = RADIUS + mix * 1.4;

        ctx.beginPath();
        ctx.fillStyle = `rgba(${r | 0}, ${g | 0}, ${b | 0}, ${alpha})`;
        ctx.arc(d.x, d.y, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", layout);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0"
    />
  );
}